import React, { useState } from 'react';
import { HStack, Input, Text } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { FormsUpdateQuestionMutationInput } from './__generated__/EditQuestionCard_updateQuestion_Mutation.graphql';
import { graphql, useFragment } from 'utils/graphClient';
import { QuestionInputs_question$key } from './__generated__/QuestionInputs_question.graphql';
import { FormQuestionType } from 'components/icons/QuestionIcon';
import { NumericInput } from '../../../../../../../components/fields/NumericInput';
import { CheckboxesInput } from '../../../../../../../components/fields/CheckboxesInput';
import uniqueId from 'lodash/uniqueId';
import { MultipleChoiceInput } from '../../../../../../../components/fields/MultipleChoiceInput';
import { PhoneNumberInput } from 'components/PhoneNumberInput';

const fragment = graphql`
  fragment QuestionInputs_question on FormQuestion {
    id
    definition {
      __typename
      ... on CustomField__String {
        id
      }
      ... on CustomField__Numeric {
        minValue
        maxValue
      }
      ... on CustomField__Checkboxes {
        id
        options {
          value
          text
        }
      }
      ... on CustomField__MultipleChoice {
        id
        options {
          value
          text
        }
      }
      ... on CustomField__Date {
        id
      }
      ... on CustomField__Time {
        id
      }
      ... on CustomField__DateTime {
        id
      }
      ... on CustomField__PhoneNumber {
        id
      }
      ... on CustomField__Email {
        id
      }
      ... on CustomField__Link {
        id
      }
    }
  }
`;

interface QuestionInputsProps {
  question: QuestionInputs_question$key;
  isDisabled: boolean;
  saveQuestion: (input: FormsUpdateQuestionMutationInput) => void;
}

export const QuestionInputs = ({
  question,
  isDisabled,
  saveQuestion,
}: QuestionInputsProps) => {
  const { id, definition } = useFragment(fragment, question);

  const isNumericType = definition?.__typename === FormQuestionType.NUMERIC;
  const [numericDefinition, setNumericDefinition] = useState({
    minValue: isNumericType ? definition.minValue : 0,
    maxValue: isNumericType ? definition.maxValue : 100,
  });

  const isCheckboxesType =
    definition?.__typename === FormQuestionType.CHECKBOXES;
  const [checkboxesDefinition, setCheckboxesDefinition] = useState({
    options: isCheckboxesType
      ? definition.options
      : [{ value: uniqueId(), text: 'New option' }],
  });

  const isMultipleChoiceType =
    definition?.__typename === FormQuestionType.MULTIPLECHOICE;
  const [multipleChoiceDefinition, setMultipleChoiceDefinition] = useState({
    options: isMultipleChoiceType
      ? definition.options
      : [{ value: uniqueId(), text: 'New option' }],
  });

  switch (definition?.__typename) {
    case FormQuestionType.TEXT:
      return (
        <Text fontSize="sm" fontStyle="italic" p={2}>{t`Long form text`}</Text>
      );
    case FormQuestionType.NUMERIC:
      return (
        <NumericInput
          value={numericDefinition}
          onChange={(newNumericDefinition) => {
            const valuesAreValid =
              !isNaN(newNumericDefinition.minValue) &&
              !isNaN(newNumericDefinition.maxValue);
            const minimumIsBelowMaximum =
              newNumericDefinition.minValue <= newNumericDefinition.maxValue;

            // give user some slack in typing
            setNumericDefinition(newNumericDefinition);

            // but enforce strict before sending it to the backend
            if (valuesAreValid && minimumIsBelowMaximum) {
              saveQuestion({
                id,
                numericDefinition: newNumericDefinition,
              });
            }
          }}
          isDisabled={isDisabled}
        />
      );
    case FormQuestionType.CHECKBOXES:
      return (
        <CheckboxesInput
          value={checkboxesDefinition}
          onChange={(newCheckboxesDefinition) => {
            setCheckboxesDefinition(newCheckboxesDefinition);
            saveQuestion({
              id,
              checkboxesDefinition: newCheckboxesDefinition,
            });
          }}
          isDisabled={isDisabled}
        />
      );

    case FormQuestionType.MULTIPLECHOICE:
      return (
        <MultipleChoiceInput
          value={multipleChoiceDefinition}
          onChange={(newMultipleChoiceDefinition) => {
            setMultipleChoiceDefinition(newMultipleChoiceDefinition);
            saveQuestion({
              id,
              multipleChoiceDefinition: newMultipleChoiceDefinition,
            });
          }}
          isDisabled={isDisabled}
        />
      );

    case FormQuestionType.DATE:
      return (
        <HStack>
          <Text
            fontSize="sm"
            fontStyle="italic"
            p={2}
          >{t`Only date picker`}</Text>
          <Input type="date" width="150px" isDisabled />
        </HStack>
      );
    case FormQuestionType.TIME:
      return (
        <HStack>
          <Text
            fontSize="sm"
            fontStyle="italic"
            p={2}
          >{t`Only time picker`}</Text>
          <Input type="time" width="100px" isDisabled />
        </HStack>
      );

    case FormQuestionType.DATETIME:
      return (
        <HStack>
          <Text
            fontSize="sm"
            fontStyle="italic"
            p={2}
          >{t`Date and time picker`}</Text>
          <Input type="date" width="150px" isDisabled />
          <Input type="time" width="100px" isDisabled />
        </HStack>
      );
    case FormQuestionType.EMAIL:
      return (
        <Text fontSize="sm" fontStyle="italic" p={2}>
          <Input type="email" isDisabled placeholder="email@example.com" />
        </Text>
      );

    case FormQuestionType.PHONE:
      return (
        <HStack>
          <PhoneNumberInput value="" handleChange={(value) => {}} isDisabled />
        </HStack>
      );

    case FormQuestionType.LINK:
      return (
        <Text fontSize="sm" fontStyle="italic" p={2}>
          <Input
            type="link"
            isDisabled
            placeholder="https://example.com/a/random/page"
          />
        </Text>
      );

    default:
      return (
        <Text
          fontSize="sm"
          textStyle="italic"
          p={2}
        >{t`Question type not supported`}</Text>
      );
  }
};
